import React, { useEffect, useRef, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Checkbox, Form, Grid } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import BrowCow from '@assets/images/brown-cow.jpg'
import InfoPopup from '~/components/InfoPopup'
import ClimaCheckService, { ClimateCheckSelectType, HeiferRaisingMethod } from '~/api/services/climaCheck'

//calfes
import BlackCalf from '@assets/images/black-calf.png'
import GrayCalf from '@assets/images/gray-calf.png'
import BrownWhiteCalf from '@assets/images/brown-white-calf.png'
import JerseyCalf from '@assets/images/joursey-farse.png'

import { handleErrors } from '~/utils/helpers'
import { useParams } from 'react-router-dom'
import { BROWN_COW_ID, individualPhases } from '../../utils'
import { StyledButton, StyledField } from '../style'
import LinkPopup from '../LinkPopup'
import WarningIcon from '../WarningIcon'
import ErrorIcon from '../ErrorIcon'

interface Props {
  setIsLoadingResults: (value: boolean) => void
  youngCattleBreeds: ClimateCheckSelectType[]
  heiferRaisingMethods: HeiferRaisingMethod[]
}

const MIN_DLG_CALVING_AGE = 24
const MAX_DLG_CALVING_AGE = 30

const ThirdStep: React.FC<Props> = (props) => {
  const { id, farmerId } = useParams()
  const { youngCattleBreeds, heiferRaisingMethods, setIsLoadingResults } = props
  const { t } = useTranslation()
  const {
    control,
    setValue,
    formState: { isSubmitted, dirtyFields },
    clearErrors,
    reset,
    getValues
  } = useFormContext()

  const [isLoadingPredefinedData, setIsLoadingPredefinedData] = useState(false)
  const isMounted = useRef(false)

  const watchBreed = useWatch({ control, name: 'breeds' })
  const watchYoungCattle = useWatch({ control, name: 'youngCattleBreedId' })
  const watchRaisesHeifers = useWatch({ control, name: 'raiseHeifersOnFarm' })
  const watchYoungCalfWeight = useWatch({ control, name: 'youngCattleWeightBeforeCalving' })
  const watchLostYoungCattle = useWatch({ control, name: 'lostYoungCattleWeightBeforeOneYear' })
  const watchLostYoungCattleAfter = useWatch({ control, name: 'lostYoungCattleWeightAfterOneYear' })
  const watchYoungCattleLossesBefOneYear = useWatch({ control, name: 'youngCattleLossesBeforeOneYear' })
  const watchYoungCattleLossesAfterOneYear = useWatch({ control, name: 'youngCattleLossesAfterOneYear' })
  const watchYoungCattleNumbers = useWatch({ control, name: 'youngCattleNumber' })
  const watchCowsNumber = useWatch({ control, name: 'cowsNumber' })
  const watchEka = useWatch({ control, name: 'firstCalvingAge' })

  useEffect(() => {
    const formValues = getValues()
    if (isSubmitted && !isNaN(watchRaisesHeifers)) {
      clearErrors('raiseHeifersOnFarm')
    }
    if (!watchRaisesHeifers) {
      reset({
        ...formValues,
        firstCalvingAge: '',
        youngCattleDieselConsumption: '',
        youngCattleLitterQuantity: '',
        youngCattleNumber: '',
        youngCattlePastureDays: '',
        youngCattleWeightBeforeCalving: '',
        rearingPhases: [],
        rearingPhasesType: '',
        calvesEnergyConsumptions: [],
        calvesHousingTypes: [],
        calvesManureStorageTypes: [],
        calvesConcentratedFeeds: [],
        calvesCoarseFeeds: []
      })
    } else if (watchBreed && watchRaisesHeifers && watchBreed[0]?.id !== BROWN_COW_ID && !formValues.firstCalvingAge) {
      fetchPredefinedData(watchBreed)
    }
  }, [watchRaisesHeifers])

  useEffect(() => {
    if (parseInt(watchYoungCattleLossesBefOneYear) === 0) {
      setValue('lostYoungCattleWeightBeforeOneYear', 0)
    }

    if (parseInt(watchYoungCattleLossesAfterOneYear) === 0) {
      setValue('lostYoungCattleWeightAfterOneYear', 0)
    }
  }, [watchYoungCattleLossesBefOneYear, watchYoungCattleLossesAfterOneYear])

  useEffect(() => {

    if (!isMounted.current) {
      isMounted.current = true
      return
    }

    const fieldNames = ['heiferRaisingMethodId', 'firstCalvingAge', 'youngCattleWeightBeforeCalving']
    const isAllFieldsEntered = watchEka && watchRaisesHeifers && watchYoungCalfWeight
    const isFieldValueChanged = Boolean(Object.keys(dirtyFields).find(dirtyFieldKey => fieldNames.find(fieldName => fieldName === dirtyFieldKey)))
    const isCalvingAgeInInterval = parseInt(watchEka) >= MIN_DLG_CALVING_AGE && parseInt(watchEka) <= MAX_DLG_CALVING_AGE
  
    const youngCattleWeightInRange = parseInt(watchYoungCalfWeight) >= 300 && parseInt(watchYoungCalfWeight) <= 800
    
    if (isAllFieldsEntered && isFieldValueChanged && isCalvingAgeInInterval && youngCattleWeightInRange) {
      fetchNewDlgFeedCalculation()
    } else if (isAllFieldsEntered && isFieldValueChanged && !isCalvingAgeInInterval) {
      setValue('rearingPhases', individualPhases(parseFloat(watchEka)))
      setValue('rearingPhasesType', 'individual')
    }
  
  }, [watchEka, watchRaisesHeifers, watchYoungCalfWeight])
  
  async function fetchNewDlgFeedCalculation() {
    try {
      const response = await ClimaCheckService.getClimaChDlgFeed(
        id,
        {
          heiferRaisingMethodId: watchRaisesHeifers,
          firstCalvingAge: watchEka?.toString()?.replace(',', '.'),
          youngCattleWeightBeforeCalving: watchYoungCalfWeight
        }
      )
      setValue('rearingPhases', response.data)
      setValue('rearingPhasesType', 'dlg')
    } catch (error) {
      handleErrors(error)
    }
  }

  async function fetchPredefinedData(value) {
    setIsLoadingPredefinedData(true)
    setIsLoadingResults(true)
    try {
      const formValues = getValues()
      const response = await ClimaCheckService.getHeifersClimaChPredData(
        id,
        {
          breeds: formValues.value,
          tax: formValues.tax,
          farmerId,
          youngCattleBreedId: watchBreed ? watchBreed[0]?.id : ''
        }
      )

      reset({
        ...formValues,
        ...response.data,
        youngCattleWeightBeforeCalving: '530',
        rearingPhases: [],
        rearingPhasesType: '',
        youngCattlePastureDays: '',
        firstCalvingAge: '',
        youngCattleDieselConsumption: ''
      })
      setValue('youngCattleBreedId', watchBreed ? watchBreed[0]?.id : '')
    } catch (error) {
      handleErrors(error)
    }
    setIsLoadingPredefinedData(false)
    setIsLoadingResults(false)
  }

  function renderFields() {
    const ekaValue = watchEka ? parseFloat(watchEka.replace(',', '.')) : 0
    return <div style={{ marginTop: '2em' }}>
      <Grid columns={3} >
        <Grid.Row style={{ alignItems: 'end' }}>
          <Grid.Column>
            <FormFields.Select
              control={control}
              name={'heiferRaisingMethodId'}
              disabled={isLoadingPredefinedData}
              withoutAsterisk
              options={heiferRaisingMethods.map(heifer => ({ value: heifer.id, text: heifer.label }))}
              label={<div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: '0.5em' }}>
                <label>{'Produktionsverfahren'}</label>
                <InfoPopup
                  style={{ minWidth: 350 }}
                  content={`Wählen Sie hier das Verfahren aus, das Sie auch im Rahmen Ihrer Berichtspflicht gemäß Düngeverordnung (DüV) zur Berechnung des N-Anfalls aus der Tierhaltung angegeben haben. 
                    Nehmen Ihre Färsen über 75 % der gesamten Grobfutter Trockenmasse über Grasprodukte von Wiesen und Weiden auf, ist Ihr Betrieb ein Grünlandbetrieb. 
                    Bei einer geringeren Aufnahme ist Ackerfutter zu wählen. Grünland extensiv bezeichnet hierbei Betriebe, deren Grobfutter von Naturschutzflächen stammt.`}
                  position={'top center'}
                  styleIcon={{ fontSize: '1.2em' }}
                />
                <LinkPopup
                  content={'DüV'}
                />
              </div>}
              decimalScale={1}
              typeInput='controled'
              noErrorMessage
              rules={{
                required: true
              }}
              placeholder={'Wählen das Produktionsverfahren'}
            />
          </Grid.Column>

          <Grid.Column>
            <FormFields.Input
              disabled={isLoadingPredefinedData}
              control={control}
              name={'firstCalvingAge'}
              noErrorMessage={!watchEka}
              decimalScale={1}
              allowDecimals
              suffix={' Monate'}
              styleErrorLabel={{ position: 'absolute', zIndex: 999, left: 20, marginTop: '0.5em', maxWidth: 300 }}
              withoutAsterisk
              label={<div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: '0.5em' }}>
                <label>{'Erstkalbealter (EKA)'}</label>
                <LinkPopup
                  content={'LKV-Bericht, Herdensoftware - "EKA"'}
                />
                {(ekaValue < 23 || ekaValue > 28) && <WarningIcon
                  control={control}
                  name='firstCalvingAge'
                  content={'Das Erstkalbealter liegt außerhalb des Normalbereichs von mindestens 23,0 Monaten und maximal 28,0 Monaten.'}
                />}
                <ErrorIcon
                  control={control}
                  name={'firstCalvingAge'}
                />
              </div>}
              typeInput='controled'
              rules={{
                validate: (value) => {
                  const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                  if (transformValue < 10 || transformValue > 35) {
                    return 'Das eingegebene Erstkalbealter muss mindestens 10,0 Monate und darf maximal 35,0 Monate betragen.'
                  }
                  return true
                },
                required: true
              }}
              placeholder={'Anzahl Monate'}
            />
          </Grid.Column>

          <Grid.Column>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column>
            <FormFields.Input
              control={control}
              disabled={isLoadingPredefinedData}
              name={'youngCattleNumber'}
              suffix={' Färse(n)'}
              withoutAsterisk
              label={<div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: '0.5em' }}>
                <label>{t('⌀ inventory')}</label>
                <InfoPopup
                  content={'29. LT bis Abkalbung; die Kälber bis 28 Tage sind dementsprechend rauszurechnen'}
                  position={'top center'}
                  styleIcon={{ fontSize: '1.2em' }}
                />
                <LinkPopup
                  content={'HITier (Bestandsregister, "weiblich, ohne Abkalbung")'}
                />
                {watchYoungCattleNumbers > 2000 && <WarningIcon
                  control={control}
                  name='youngCattleNumber'
                  content={'Der ⌀-Bestand scheint sehr hoch zu sein. Sind Sie sicher, dass dieser Wert stimmt?'}
                />}
                <ErrorIcon
                  control={control}
                  name={'youngCattleNumber'}
                />
              </div>}
              typeInput='controled'
              noErrorMessage
              rules={{
                required: true,
                min: 3,
                max: 6000,
                validate: (value) => {
                  const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                  if (transformValue > parseInt(watchCowsNumber) * 2.1) {
                    return `Der ⌀-Bestand Färsen liegt über dem  ⌀-Bestand Milchkühe(${parseInt(watchCowsNumber)}) 110%.`
                  }
                  return true
                }
              }}
              placeholder={'Anzahl Färse(n)'}
            />
          </Grid.Column>

          <Grid.Column>
            <FormFields.Input
              disabled={isLoadingPredefinedData}
              control={control}
              noErrorMessage
              name={'youngCattleLossesBeforeOneYear'}
              styleErrorLabel={{ position: 'absolute', zIndex: 999, left: 20, marginTop: '0.5em', maxWidth: 300 }}
              suffix={' Färse(n)'}
              withoutAsterisk
              label={<div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: '0.5em' }}>
                <label>{'Anzahl der Verluste von 29. LT bis 1 Jahr'}</label>
                <InfoPopup
                  content={'3-4 Monate: 100 kg Lebendmasse, 5-6 Monate: 200 kg LM, 4-7 Monate: 150-249 kg LM, 8-11 Monate: 250-349 kg LM, 12-15 Monate: 350-449  kg LM'}
                  position={'top center'}
                  styleIcon={{ fontSize: '1.2em' }}
                />
                <LinkPopup
                  content={'HITier, Kennung "VE" und "TOE" oder BZA'}
                />
                {watchYoungCattleLossesBefOneYear > watchYoungCattleNumbers * 0.2 && <WarningIcon
                  control={control}
                  name='youngCattleLossesBeforeOneYear'
                  content={'Die Anzahl der Verluste liegt über dem Normalbereich von 20% des Gesamtbestandes der Färsen.'}
                />}
              </div>}
              typeInput='controled'
              rules={{
                required: true,
                min: 0,
                max: 100
              }}
              placeholder={'Anzahl Färse(n)'}
            />
          </Grid.Column>

          <Grid.Column>
            <FormFields.Input
              control={control}
              disabled={isLoadingPredefinedData}
              noErrorMessage
              name={'youngCattleLossesAfterOneYear'}
              typeInput='controled'
              withoutAsterisk
              rules={{
                required: true,
                min: 0,
                max: 100
              }}
              suffix=' Färse(n)'
              label={<div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: '0.5em' }}>
                <label>{'Anzahl der Verluste über 1 Jahr'}</label>
                <LinkPopup
                  content={'HITier, Kennung "VE" und "TOE" oder BZA'}
                />
                {watchYoungCattleLossesAfterOneYear > watchYoungCattleNumbers * 0.2 && <WarningIcon
                  control={control}
                  name='youngCattleLossesAfterOneYear'
                  content={'Die Anzahl der Verluste liegt über dem Normalbereich von 20% des Gesamtbestandes der Färsen.'}
                />}
              </div>}
              placeholder={'Anzahl Färse(n)'}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column>
            <StyledField>
              <label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div>{'⌀-Gewicht vor Abkalbung'}</div>
                  <InfoPopup
                    content={`Vorgeschlagener Wert: 84 % des Altkuhgewichtes. Jungrinder sind noch nicht ausgewachsen und wiegen deshalb weniger als Ihre Altkühe.
                    Inkl. Kalb und Fruchtwasser sind die Tiere allerdings ca. 70 - 90 kg schwerer als kurz nach dem Kalben.`}
                    position={'top center'}
                    styleIcon={{ fontSize: '1.2em' }}
                  />
                  {(watchYoungCalfWeight < 400 || watchYoungCalfWeight > 650) && <WarningIcon
                    control={control}
                    name='youngCattleWeightBeforeCalving'
                    content={'Das Gewicht vor Abkalbung liegt außerhalb des Normalbereichs von mindestens 400 kg und maximal 650 kg.'}
                  />}
                  <ErrorIcon
                    control={control}
                    name={'youngCattleWeightBeforeCalving'}
                  />
                </div>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginTop: 8 }}>
                <StyledButton
                  type='button'
                  icon='minus'
                  disabled={watchYoungCalfWeight - 10 <= 0 || !watchYoungCalfWeight}
                  onClick={() => {
                    if (!watchYoungCalfWeight) {
                      return
                    }
                    const numberYoungCattle = parseInt(watchYoungCalfWeight) - 10
                    if (numberYoungCattle >= 300 && numberYoungCattle <= 800) {
                      clearErrors('youngCattleWeightBeforeCalving')
                    }
                    setValue('youngCattleWeightBeforeCalving', numberYoungCattle)
                  }}
                />

                <FormFields.Input
                  control={control}
                  disabled={isLoadingPredefinedData}
                  name={'youngCattleWeightBeforeCalving'}
                  noErrorMessage={!watchYoungCalfWeight}
                  typeInput='controled'
                  withoutAsterisk
                  rules={{
                    validate: (value) => {
                      const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                      if (transformValue < 300 || transformValue > 800) {
                        return 'Das Gewicht vor Abkalbung muss mindestens 300 kg und darf maximal 800 kg betragen.'
                      }
                      return true
                    },
                    required: true
                  }}
                  suffix=' kg Lebendmasse'
                  placeholder={'in kg Lebendmasse'}
                />
                <StyledButton
                  type='button'
                  onClick={() => {
                    const numberYoungCattle = parseInt(watchYoungCalfWeight || 0) + 10
                    if (numberYoungCattle >= 300 && numberYoungCattle <= 800) {
                      clearErrors('youngCattleWeightBeforeCalving')
                    }
                    setValue('youngCattleWeightBeforeCalving', numberYoungCattle)
                  }}
                  icon='add'
                />
              </div>
            </StyledField>
          </Grid.Column>

          <Grid.Column>
            <StyledField>
              <label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div>{'⌀-Gewicht der Verluste von 29. LT bis 1 Jahr'}</div>
                  <InfoPopup
                    content={'3-4 Monate: 100 kg LM, 5-6 Monate: 200 kg LM, 4-7 Monate: 150-249 kg LM'}
                    position={'top center'}
                    styleIcon={{ fontSize: '1.2em' }}
                    style={{ minWidth: 350 }}
                  />
                  {(watchLostYoungCattle < 70 || watchLostYoungCattle > 400) && parseInt(watchYoungCattleLossesBefOneYear) > 0 && <WarningIcon
                    control={control}
                    name='lostYoungCattleWeightBeforeOneYear'
                    content={'Das Gewicht der Verluste liegt außerhalb des Normalbereichs von mindestens 70 kg und maximal 400 kg.'}
                  />}
                  <ErrorIcon
                    control={control}
                    name={'lostYoungCattleWeightBeforeOneYear'}
                  />
                </div>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginTop: 8 }}>
                <StyledButton
                  type='button'
                  icon='minus'
                  disabled={(watchLostYoungCattle - 10 <= 0 || !watchLostYoungCattle) || parseInt(watchYoungCattleLossesBefOneYear) === 0}
                  onClick={() => {
                    if (!watchLostYoungCattle) {
                      return
                    }
                    const numberYoungCattle = parseInt(watchLostYoungCattle) - 10
                    if (numberYoungCattle >= 10 && numberYoungCattle <= 999) {
                      clearErrors('lostYoungCattleWeightBeforeOneYear')
                    }
                    setValue('lostYoungCattleWeightBeforeOneYear', numberYoungCattle)
                  }}
                />
                <FormFields.Input
                  control={control}
                  disabled={isLoadingPredefinedData || parseInt(watchYoungCattleLossesBefOneYear) === 0}
                  name={'lostYoungCattleWeightBeforeOneYear'}
                  suffix={' kg Lebendmasse'}
                  styleErrorLabel={{ position: 'absolute', zIndex: 999, left: 20, marginTop: '0.5em', maxWidth: 300 }}
                  withoutAsterisk
                  typeInput='controled'
                  noErrorMessage
                  rules={{
                    validate: (value) => {
                      if (parseInt(watchYoungCattleLossesBefOneYear) === 0) {
                        return true
                      }
                      const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                      if (transformValue < 10 || transformValue > 999) {
                        return 'Gewicht der Verluste muss mindestens 10 kg und darf maximal 999 kg betragen.'
                      }
                      return true
                    },
                    required: true,
                    max: {
                      value: watchLostYoungCattleAfter && watchLostYoungCattleAfter > 0 ? watchLostYoungCattleAfter : 999,
                      message: 'Das eingegebene Gewicht für Verluste bis 1 Jahr kann nicht größer sein als für die Verluste über einem Jahr.'
                    }
                  }}
                  placeholder={'in kg Lebendmasse'}
                />
                <StyledButton
                  type='button'
                  disabled={parseInt(watchYoungCattleLossesBefOneYear) === 0}
                  onClick={() => {
                    const numberYoungCattle = parseInt(watchLostYoungCattle || 0) + 10
                    if (numberYoungCattle >= 10 && numberYoungCattle <= 999) {
                      clearErrors('lostYoungCattleWeightBeforeOneYear')
                    }
                    setValue('lostYoungCattleWeightBeforeOneYear', numberYoungCattle)
                  }}
                  icon='add'
                />
              </div>
            </StyledField>
          </Grid.Column>
          <Grid.Column>
            <StyledField>
              <label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div>{'⌀-Gewicht der Verluste über 1 Jahr'}</div>
                  <InfoPopup
                    content={'12-15 Monate: 350-449  kg LM, 16-20 Monate: 450-59 kg, 21-24 Monate: > 550'}
                    position={'top center'}
                    styleIcon={{ fontSize: '1.2em' }}
                    style={{ minWidth: 350 }}
                  />
                  {(watchLostYoungCattleAfter < 250 || watchLostYoungCattleAfter > 650) && parseInt(watchYoungCattleLossesAfterOneYear) > 0 && <WarningIcon
                    control={control}
                    name='lostYoungCattleWeightAfterOneYear'
                    content={'Das Gewicht der Verluste liegt außerhalb des Normalbereichs von mindestens 250 kg und maximal 650 kg.'}
                  />}
                  <ErrorIcon
                    control={control}
                    name={'lostYoungCattleWeightAfterOneYear'}
                  />
                </div>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginTop: 8 }}>
                <StyledButton
                  type='button'
                  icon='minus'
                  disabled={(watchLostYoungCattleAfter - 10 <= 0 || !watchLostYoungCattleAfter) || parseInt(watchYoungCattleLossesAfterOneYear) === 0}
                  onClick={() => {
                    if (!watchLostYoungCattleAfter) {
                      return
                    }
                    const numberYoungCattle = parseInt(watchLostYoungCattleAfter) - 10
                    if (numberYoungCattle >= 200 && numberYoungCattle <= 999) {
                      clearErrors('lostYoungCattleWeightAfterOneYear')
                    }
                    setValue('lostYoungCattleWeightAfterOneYear', numberYoungCattle)
                  }}
                />
                <FormFields.Input
                  control={control}
                  disabled={isLoadingPredefinedData || parseInt(watchYoungCattleLossesAfterOneYear) === 0}
                  name={'lostYoungCattleWeightAfterOneYear'}
                  noErrorMessage
                  styleErrorLabel={{ position: 'absolute', zIndex: 999, left: 20, marginTop: '0.5em', maxWidth: 300 }}
                  typeInput='controled'
                  withoutAsterisk
                  rules={{
                    validate: (value) => {
                      if (parseInt(watchYoungCattleLossesAfterOneYear) === 0) {
                        return true
                      }
                      const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                      if (transformValue > 999) {
                        return 'Das Gewicht der Verluste liegt außerhalb des maximal 999 kg.'
                      }
                      return true
                    },
                    required: true
                  }}
                  suffix=' kg Lebendmasse'
                  placeholder={'in kg Lebendmasse'}
                />
                <StyledButton
                  type='button'
                  disabled={parseInt(watchYoungCattleLossesAfterOneYear) === 0}
                  onClick={() => {
                    const numberYoungCattle = parseInt(watchLostYoungCattleAfter || 0) + 10
                    if (numberYoungCattle >= 200 && numberYoungCattle <= 999) {
                      clearErrors('lostYoungCattleWeightAfterOneYear')
                    }
                    setValue('lostYoungCattleWeightAfterOneYear', parseInt(watchLostYoungCattleAfter || 0) + 10)
                  }}
                  icon='add'
                />
              </div>
            </StyledField>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  }

  function getSourceCalf(breed) {
    switch (breed.id) {
      case 1:
        return BrownWhiteCalf
      case 2:
        return GrayCalf
      case 3:
        return BrowCow
      case 4:
        return BlackCalf
      case 5:
        return JerseyCalf
    }
  }

  function renderRace() {
    return <Form.Field>
      <label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div>{t('Race')}</div>
          <InfoPopup
            content={t('First, click on the main breed you have on your farm.')}
            styleIcon={{ fontSize: '1.2em' }}
            position={'top center'}
          />
        </div>
      </label>
      <Form.Group widths={'equal'} style={{ alignItems: 'center' }}>
        {youngCattleBreeds?.map((youngCattle) => {
          return <Form.Field key={youngCattle.id} style={{ position: 'relative' }}>
            <Checkbox
              disabled={isLoadingPredefinedData}
              label={
                <label style={{ whiteSpace: 'nowrap', minWidth: 200 }}>
                  <img
                    src={getSourceCalf(youngCattle)}
                    style={{
                      width: 100,
                      marginRight: 10,
                      height: 100,
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: 70,
                      display: 'flex'
                    }}>
                    <div>{youngCattle.label}</div>
                  </div>

                </label>
              }
              checked={watchYoungCattle === youngCattle.id}
              onChange={() => {
                fetchPredefinedData(youngCattle.id)
              }
              }
            />
          </Form.Field>
        })}
      </Form.Group>
    </Form.Field>
  }

  function renderHeifersFarmSelect() {
    return <Controller
      rules={{ required: true }}
      name='raiseHeifersOnFarm'
      render={({ fieldState: { error } }) => (
        <Form.Field error={Boolean(error)}>
          <label>{'Ziehen Sie Ihre Färsen auf dem Betrieb selber auf?'}</label>
          <div style={{ display: 'flex', gap: 20 }}>
            <Checkbox
              radio
              label='Ja'
              disabled={isLoadingPredefinedData}
              checked={watchRaisesHeifers === 1}
              onClick={() => setValue('raiseHeifersOnFarm', 1)}
            />
            <Checkbox
              radio
              disabled={isLoadingPredefinedData}
              label='Nein'
              checked={watchRaisesHeifers === 0}
              onClick={() => setValue('raiseHeifersOnFarm', 0)}
            />
          </div>
        </Form.Field>
      )}
    />
  }

  const displayRaceSelection = watchBreed === BROWN_COW_ID

  return <>
    {renderHeifersFarmSelect()}
    {watchRaisesHeifers === 1 && <>
      {displayRaceSelection && renderRace()}
      {watchYoungCattle && youngCattleBreeds && renderFields()}
    </>}
  </>
}
export default ThirdStep